import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '../../paths';
import { useSidePanel } from '../../providers/SidePanelProvider';

/**
 * Open an alert in the assistant thread from a surface outside the assistant
 * pane — a record page's `RecordAlertBanner`.
 *
 * Two steps, because the pane sits in two places. Recording the alert and
 * opening the `xl+` column is the whole move at that size: the pane is mounted
 * in the column and only has to open. Below `xl` the pane is the `/assistant`
 * route, so the click also has to navigate there — and the alert survives that
 * navigation because it is held on the panel context rather than inside the
 * pane.
 *
 * Surface first, then navigate: it also pre-arms the panel so a later widen
 * lands on the thread.
 *
 * @returns A click handler taking the alert's id and whether the row was still
 *   unread when it was clicked. The expand itself is issued by {@link useDrainPendingAlertExpand} once the pane
 *   is on screen.
 */
export function useOpenAlertInAssistant(): (alertId: string, wasUnread: boolean) => void {
  const navigate = useNavigate();
  const isXl = useBreakpoint('xl');
  const { openAlertInAssistant } = useSidePanel();

  return React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      openAlertInAssistant(alertId, wasUnread);

      if (!isXl) {
        void navigate(PATHS.ASSISTANT);
      }
    },
    [openAlertInAssistant, isXl, navigate]
  );
}
